import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Controller} from 'react-hook-form';
import get from 'lodash/get';
import {v4 as uuidv4} from 'uuid';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Button from '@material-ui/core/Button';
import {Add, DeleteOutlined, EditOutlined} from '@material-ui/icons';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import GenericTab from '../../../shared/generictab/GenericTab.js';
import Separator from '../../../shared/separator/Separator.js';
import InstructionMessage from '../../../shared/instructionmessage/InstructionMessage.js';
import BlocoErro from '../../../shared/listaerros/BlocoErro.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {ValorMonetarioNumberFormatCustom} from '../../../shared/formatters/index.js';
import DialogRenda from '../../../publica/Page/inscricao/DialogRenda.jsx';
import DialogMembroFamiliar from '../../../publica/Page/inscricao/DialogMembroFamiliar.jsx';
import DialogParenteBeneficiario from '../../../publica/Page/inscricao/DialogParenteBeneficiario.jsx';
import DialogEnderecoComposicaoFamiliar from '../../../publica/Page/inscricao/DialogEnderecoComposicaoFamiliar.jsx';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import {consolidarOcupacoes, descricaoTipoRenda, formatarMoeda, somarRendas} from '../../../publica/Page/inscricao/rendaMembro.js';
import {isSameMember} from '../../../publica/Page/inscricao/membroFamiliar.js';
import useErrorMessage from '../../../../hooks/useErrorMessage.js';
import {buttonTableCell, containedButton, tituloSecao} from '../../../../utils/styles.js';
import {calcularRendaInscricao, centavosDoMembro} from '../../components/inscricao/rendaInscricao.js';
import {formatarCep} from '../../components/inscricao/inscricaoAdministrativaPayload.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO} from '../../components/paginacao.js';
import {indiceSecaoVisivel} from '../../components/inscricao/inscricaoAdministrativaSchema.js';

/* Mesmas larguras e títulos da etapa Socioeconômico da inscrição pública. */
const useStyles = makeStyles(theme => ({
    tabs: {
        width: '100%',
        minWidth: '180px',
        maxWidth: '220px',
        marginRight: theme.spacing(2),
        borderRight: `1px solid ${theme.palette.divider}`,
        '& .MuiTab-root': {
            '&.MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
                width: '100%',
                minWidth: '180px',
                maxWidth: '220px',
                alignItems: 'flex-start',
                textAlign: 'left'
            }
        }
    },
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    ...buttonTableCell(theme),
    ...containedButton(theme, false, false)
}));

const OPCOES_IR = [{valor: 'S', descricao: 'Sim'}, {valor: 'N', descricao: 'Não'}, {valor: 'I', descricao: 'Isento'}];
const SIM_NAO = [{valor: 'true', descricao: 'Sim'}, {valor: 'false', descricao: 'Não'}];
const DIALOGOS_FECHADOS = {renda: null, parente: null, endereco: null, membro: null};

/*
 * Etapa "Socioeconômico": Declarações e Moradia, Rendas (com o cálculo, só
 * exibição), Grupo Familiar (endereços e integrantes) e Parentes
 * Beneficiários, no visual da inscrição pública e com os diálogos dela.
 */
export default function EtapaSocioeconomico(props) {
    const {
        control,
        errors,
        watch,
        setValue,
        secoesComErro,
        secaoFoco,
        regras,
        tiposHabitacao = [],
        tiposMoradia = [],
        grausParentesco = [],
        tiposEndereco = []
    } = props;
    const classes = useStyles();
    const tabRef = useRef();
    const confirmation = useConfirmationDialog();
    const showErrorMessage = useErrorMessage();
    // Índice do item em edição em cada diálogo: -1 para adicionar, null com o diálogo fechado.
    const [dialogos, setDialogos] = useState(DIALOGOS_FECHADOS);

    const parentes = watch('parentesBeneficiarios') || [];
    const rendas = watch('renda') || [];
    const enderecos = watch('enderecoComposicaoFamiliares') || [];
    const membros = watch('composicaoFamiliar') || [];
    const anexos = watch('inscricaoAnexos') || [];
    const vlrPensao = watch('vlrPensao');
    const vlrPensaoFilho = watch('vlrPensaoFilho');
    const calculo = calcularRendaInscricao({renda: rendas, composicaoFamiliar: membros, vlrPensao, vlrPensaoFilho});

    const erro = nome => get(errors, nome);
    const abrir = (dialogo, indice) => () => setDialogos(atual => ({...atual, [dialogo]: indice}));
    const fechar = dialogo => () => setDialogos(atual => ({...atual, [dialogo]: null}));
    const definirLista = (nome, lista) => setValue(nome, lista, {shouldDirty: true});

    const salvarItem = (nome, lista, dialogo, montar) => form => {
        const indice = dialogos[dialogo];
        const atual = [...lista];
        const existente = indice > -1 ? atual[indice] : null;
        const item = montar(form, existente);
        if (existente) {
            atual[indice] = item;
        } else {
            atual.push(item);
        }
        definirLista(nome, atual);
        fechar(dialogo)();
    };

    const removerItem = (nome, lista, indice, mensagem) => {
        confirmation(mensagem, () => definirLista(nome, lista.filter((item, posicao) => posicao !== indice)));
    };

    const grauPorId = id => grausParentesco.find(item => String(item.id) === String(id)) || null;

    const salvarParente = salvarItem('parentesBeneficiarios', parentes, 'parente', (form, existente) => ({
        ...(existente || {}),
        nome: form.nome,
        bolsa: form.bolsa,
        grauParentesco: grauPorId(form.grauParentescoId)
    }));

    const salvarRenda = salvarItem('renda', rendas, 'renda', (form, existente) => ({
        ...(existente || {}),
        tpRenda: form.tpRenda,
        ocupacao: String(form.ocupacao || '').trim(),
        vlrRenda: parseFloat(form.vlrRenda) || 0
    }));

    const salvarEndereco = salvarItem('enderecoComposicaoFamiliares', enderecos, 'endereco', (form, existente) => ({
        ...(existente || {}),
        tipoEndereco: form.tipoEndereco,
        cep: form.cep,
        logradouro: form.logradouro,
        numero: form.numero,
        complemento: form.complemento,
        bairro: form.bairro,
        cidade: form.cidade,
        uf: form.uf
    }));

    const salvarMembro = salvarItem('composicaoFamiliar', membros, 'membro', (form, existente) => {
        const rendasMembro = (form.rendas || []).map(renda => ({...renda, vlrRenda: parseFloat(renda.vlrRenda) || 0}));
        return {
            ...(existente || {chaveLocal: uuidv4()}),
            descricao: form.descricao,
            rendas: rendasMembro,
            rendaBruta: somarRendas(rendasMembro),
            ocupacao: consolidarOcupacoes(rendasMembro),
            grauParentesco: grauPorId(form.grauParentescoId),
            enderecoComposicaoFamiliar: form.enderecoComposicaoFamiliar,
            cpf: form.cpf || null,
            rg: form.rg || null,
            dataNascimento: form.dataNascimento || null,
            declaracaoIR: form.declaracaoIR || 'N'
        };
    });

    /* Integrante com documento anexado não sai daqui: o documento ficaria sem dono. */
    const removerMembro = indice => {
        const membro = membros[indice];
        if (anexos.some(anexo => anexo.composicaoFamiliar && isSameMember(anexo.composicaoFamiliar, membro))) {
            showErrorMessage(`${membro.descricao} tem documento anexado e não pode ser removido. Remova os documentos dele na etapa Documentos antes.`);
            return;
        }
        removerItem('composicaoFamiliar', membros, indice, 'Tem certeza que deseja remover o integrante?');
    };

    /* Endereço usado por um integrante não pode ser removido. */
    const removerEndereco = indice => {
        const endereco = enderecos[indice];
        const usado = membros.some(membro => membro.enderecoComposicaoFamiliar && (
            membro.enderecoComposicaoFamiliar === endereco
            || (endereco.id && String(membro.enderecoComposicaoFamiliar.id) === String(endereco.id))
        ));
        if (usado) {
            showErrorMessage('O endereço está vinculado a um integrante do grupo familiar e não pode ser removido.');
            return;
        }
        removerItem('enderecoComposicaoFamiliares', enderecos, indice, 'Tem certeza que deseja remover o endereço?');
    };

    const descricaoTipoEndereco = tipo => {
        const nome = tipo?.nome || tipo;
        const encontrado = tiposEndereco.find(item => item.nome === nome);
        return encontrado ? encontrado.descricao : (tipo?.descricao || nome || '');
    };

    const colunaAcoes = (dialogo, lista, aoRemover) => criarColunaOpcoes(row => {
        const indice = lista.indexOf(row);
        return [
            {titulo: 'Editar', icone: <EditOutlined/>, onClick: abrir(dialogo, indice)},
            {titulo: 'Remover', icone: <DeleteOutlined/>, onClick: () => aoRemover(indice)}
        ];
    }, {className: classes.buttonTableCell});

    const botaoAdicionar = dialogo => (
        <Button variant='contained' className={classes.containedButton} startIcon={<Add/>} onClick={abrir(dialogo, -1)}>
            Adicionar
        </Button>
    );

    /* Sem comparatorKeys: a linha usa o índice como chave, já que itens novos não têm id e podem se repetir. */
    const lista = ({secao, campo, titulo, orientacoes, rows, columns, dialogo, vazio}) => (
        <Grid item xs={12}>
            <BlocoErro erro={!!erro(campo)}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>{titulo}</h4>
                        <InstructionMessage
                            title={`Orientações sobre ${orientacoes.titulo}`}
                            type='info'
                            variant='standard'
                            collapsible={true}
                            defaultExpanded={true}
                            items={orientacoes.itens}/>
                    </Grid>
                    <Grid item xs={12}>
                        <DataGrid
                            key={secao}
                            toolbarContent={botaoAdicionar(dialogo)}
                            tableContainerProps={{component: 'div'}}
                            rows={rows}
                            columns={columns}
                            paginate
                            paginateOptions={PAGINACAO_PADRAO}
                            noRecordsText={vazio}/>
                    </Grid>
                </Grid>
            </BlocoErro>
        </Grid>
    );

    const titulo = texto => (
        <Grid item xs={12}>
            <h4 className={classes.sectionTitle}>{texto}</h4>
        </Grid>
    );

    const campoRadio = (nome, rotulo, opcoes) => (
        <Grid item xs={12} sm={6}>
            <LabelValue label={rotulo}>
                <FormControl component='fieldset' error={!!erro(nome)}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <RadioGroup row name={field.name} value={field.value ?? ''} onChange={evento => field.onChange(evento.target.value)}>
                                {opcoes.map(opcao => (
                                    <FormControlLabel key={opcao.valor} value={opcao.valor} control={<Radio color='primary'/>} label={opcao.descricao}/>
                                ))}
                            </RadioGroup>
                        )}/>
                    {!!erro(nome) && <FormHelperText error>{erro(nome).message}</FormHelperText>}
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const campoSelect = (nome, rotulo, opcoes) => (
        <Grid item xs={12} sm={6}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro(nome)}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <Select {...field} value={field.value ?? ''} displayEmpty>
                                <MenuItem value=''><em>Selecione...</em></MenuItem>
                                {opcoes.map(opcao => (
                                    <MenuItem key={opcao.id} value={String(opcao.id)}>{opcao.descricao}</MenuItem>
                                ))}
                            </Select>
                        )}/>
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const campoValor = (nome, rotulo, sm) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro(nome)}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <OutlinedInput
                                name={field.name}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                inputRef={field.ref}
                                inputComponent={ValorMonetarioNumberFormatCustom}
                                startAdornment={<InputAdornment position='start'>R$</InputAdornment>}/>
                        )}/>
                    {!!erro(nome) && <FormHelperText error>{erro(nome).message}</FormHelperText>}
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const secoes = [
        {
            id: 'declaracaoMoradia',
            label: 'Declaração e Moradia',
            content: (
                <Grid container spacing={3}>
                    {titulo('Declarações')}
                    {campoRadio('declaracaoIR', 'Declara ou declarou imposto de renda no último ano fiscal?', OPCOES_IR)}
                    {regras.exibirCadUnico && campoRadio('cadUnicoOuTransferenciaRenda',
                        'Participa do CadÚnico ou outros programas de transferência de renda? *', SIM_NAO)}
                    <Separator noCenterText/>
                    {titulo('Moradia')}
                    {campoSelect('tipoHabitacao', 'Tipo de habitação', tiposHabitacao)}
                    {campoSelect('tipoMoradia', 'Tipo de moradia', tiposMoradia)}
                </Grid>
            )
        },
        {
            id: 'rendas',
            label: 'Minhas Rendas',
            content: (
                <Grid container spacing={3}>
                    {lista({
                        secao: 'renda',
                        campo: 'renda',
                        titulo: 'Minhas Fontes de Renda',
                        orientacoes: {
                            titulo: 'Minhas Fontes de Renda',
                            itens: [
                                'Cadastre todas as fontes de renda ativa do inscrito (trabalho, estágio etc.);',
                                'Sem renda própria, não é necessário adicionar nenhum item nesta tabela.'
                            ]
                        },
                        rows: rendas,
                        columns: [
                            {label: 'Tipo', field: 'tpRenda', valueGetter: valor => ouTraco(descricaoTipoRenda(valor))},
                            {label: 'Ocupação', field: 'ocupacao', valueGetter: valor => ouTraco(valor)},
                            {label: 'Valor Bruto', field: 'vlrRenda', valueGetter: valor => formatarMoeda(valor)},
                            colunaAcoes('renda', rendas, indice => removerItem('renda', rendas, indice, 'Tem certeza que deseja remover a renda?'))
                        ],
                        dialogo: 'renda',
                        vazio: 'Nenhuma renda cadastrada.'
                    })}
                    <Separator noCenterText/>
                    {titulo('Outros Tipos de Renda')}
                    {campoValor('vlrPensao', 'Valor que recebe de pensão alimentícia', 5)}
                    {campoValor('vlrPensaoFilho', 'Valor recebido pelo(s) filho(s) de pensão alimentícia', 7)}
                    {regras.exibirDisponibilidade && (
                        <>
                            <Separator noCenterText/>
                            {titulo('Outras Informações')}
                            {campoValor('disponibilidade', 'Valor disponível para pagamento do curso', 12)}
                        </>
                    )}
                    <Separator noCenterText/>
                    {titulo('Cálculo')}
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Renda familiar bruta mensal' value={formatarMoeda(calculo.rendaFamiliarBruta)}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Renda bruta mensal' value={formatarMoeda(calculo.rendaBrutaTotal)}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Renda familiar bruta mensal per capita' value={formatarMoeda(calculo.rendaPerCapita)}/>
                    </Grid>
                </Grid>
            )
        },
        {
            id: 'grupoFamiliar',
            label: 'Grupo Familiar',
            content: (
                <Grid container spacing={3}>
                    {lista({
                        secao: 'enderecos',
                        campo: 'enderecoComposicaoFamiliares',
                        titulo: 'Endereços do Grupo Familiar',
                        orientacoes: {
                            titulo: 'Endereço do Grupo Familiar',
                            itens: [
                                'Cadastre o(s) endereço(s) onde os membros do grupo familiar residem;',
                                'É possível associar o mesmo endereço a vários integrantes.'
                            ]
                        },
                        rows: enderecos,
                        columns: [
                            {label: 'Tipo de Endereço', field: 'tipoEndereco', valueGetter: valor => ouTraco(descricaoTipoEndereco(valor))},
                            {label: 'Logradouro', field: 'logradouro', valueGetter: (valor, row) => ouTraco([row.logradouro, row.numero].filter(Boolean).join(', '))},
                            {label: 'Bairro', field: 'bairro', valueGetter: valor => ouTraco(valor)},
                            {
                                label: 'Cidade/UF', field: 'cidade',
                                valueGetter: (valor, row) => ouTraco([row.cidade?.nome || row.nomeCidade, row.uf || row.cidade?.uf?.uf].filter(Boolean).join(' - '))
                            },
                            {label: 'CEP', field: 'cep', valueGetter: valor => ouTraco(formatarCep(valor))},
                            colunaAcoes('endereco', enderecos, removerEndereco)
                        ],
                        dialogo: 'endereco',
                        vazio: 'Nenhum endereço registrado.'
                    })}
                    <Separator noCenterText/>
                    {lista({
                        secao: 'membros',
                        campo: 'composicaoFamiliar',
                        titulo: 'Composição do Grupo Familiar',
                        orientacoes: {
                            titulo: 'Grupo familiar',
                            itens: [
                                'É importante que sejam informados todos os integrantes do grupo familiar;',
                                'Clique no botão Adicionar para incluir cada integrante;',
                                'Na inclusão do integrante, adicione cada ocupação/renda dele; quem não possui renda pode ficar sem ocupação informada;',
                                'Entende-se como grupo familiar a unidade nuclear composta por uma ou mais pessoas, eventualmente ampliada por outras pessoas que contribuam para o rendimento ou tenham suas despesas atendidas por aquela unidade familiar, todas moradoras em um mesmo domicílio.'
                            ]
                        },
                        rows: membros,
                        columns: [
                            {label: 'Nome', field: 'descricao', valueGetter: valor => ouTraco(valor)},
                            {label: 'Parentesco', field: 'grauParentesco.descricao', valueGetter: (valor, row) => ouTraco(row.grauParentesco?.descricao)},
                            {label: 'Ocupações', field: 'ocupacao', valueGetter: valor => valor || 'Nenhuma informada'},
                            {label: 'Renda Bruta', field: 'rendaBruta', valueGetter: (valor, row) => formatarMoeda(centavosDoMembro(row) / 100)},
                            colunaAcoes('membro', membros, removerMembro)
                        ],
                        dialogo: 'membro',
                        vazio: 'Nenhum membro registrado.'
                    })}
                </Grid>
            )
        },
        {
            id: 'parentes',
            label: 'Parentes UNAERP',
            content: (
                <Grid container spacing={3}>
                    {lista({
                        secao: 'parentes',
                        campo: 'parentesBeneficiarios',
                        titulo: 'Parentes Beneficiários (que possuem bolsa na UNAERP)',
                        orientacoes: {
                            titulo: 'Parente Beneficiário',
                            itens: [
                                'Informe todos os parentes que possuem bolsa na UNAERP;',
                                'Clique no botão Adicionar para incluir cada parente que possui bolsa.'
                            ]
                        },
                        rows: parentes,
                        columns: [
                            {label: 'Nome', field: 'nome', valueGetter: valor => ouTraco(valor)},
                            {label: 'Grau de Parentesco', field: 'grauParentesco.descricao', valueGetter: (valor, row) => ouTraco(row.grauParentesco?.descricao)},
                            {label: 'Bolsa/Desconto', field: 'bolsa', valueGetter: valor => valor || 'Não informado'},
                            colunaAcoes('parente', parentes, indice => removerItem('parentesBeneficiarios', parentes, indice, 'Tem certeza que deseja remover o parente beneficiário?'))
                        ],
                        dialogo: 'parente',
                        vazio: 'Nenhum parente registrado.'
                    })}
                </Grid>
            )
        }
    ].map(secao => ({...secao, hasError: secoesComErro.has(secao.id)}));

    useEffect(() => {
        if (!secaoFoco) return;
        const indice = indiceSecaoVisivel(secoes, secaoFoco.secao);
        if (indice >= 0 && tabRef.current) {
            tabRef.current.setTabIndex(indice);
        }
    }, [secaoFoco]);

    return (
        <>
            <GenericTab
                ref={tabRef}
                orientation='vertical'
                noAppBar
                indicatorColor='primary'
                textColor='primary'
                scrollButtons='auto'
                className={classes.tabs}
                content={secoes}/>

            <DialogParenteBeneficiario
                open={dialogos.parente !== null}
                onClose={fechar('parente')}
                onSave={salvarParente}
                initialValues={dialogos.parente > -1 ? parentes[dialogos.parente] : null}
                grausParentesco={grausParentesco}/>
            <DialogRenda
                open={dialogos.renda !== null}
                onClose={fechar('renda')}
                onSave={salvarRenda}
                initialValues={dialogos.renda > -1 ? rendas[dialogos.renda] : null}/>
            <DialogEnderecoComposicaoFamiliar
                open={dialogos.endereco !== null}
                onClose={fechar('endereco')}
                onSave={salvarEndereco}
                initialValues={dialogos.endereco > -1 ? enderecos[dialogos.endereco] : null}
                tipos={tiposEndereco}
                enderecos={enderecos}/>
            <DialogMembroFamiliar
                open={dialogos.membro !== null}
                onClose={fechar('membro')}
                onSave={salvarMembro}
                initialValues={dialogos.membro > -1 ? membros[dialogos.membro] : null}
                enderecos={enderecos}
                tiposEndereco={tiposEndereco}
                grausParentesco={grausParentesco}/>
        </>
    );
}

EtapaSocioeconomico.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    secoesComErro: PropTypes.instanceOf(Set).isRequired,
    secaoFoco: PropTypes.object,
    regras: PropTypes.object.isRequired,
    tiposHabitacao: PropTypes.array,
    tiposMoradia: PropTypes.array,
    grausParentesco: PropTypes.array,
    tiposEndereco: PropTypes.array
};
